import type React from "react";
import Layout from "../../../components/site/Layout/Layout";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "../../../components/ui/accordion";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "../../../components/ui/tabs";

const FAQPage: React.FC = () => {
	const faqs = [
		{
			id: "general",
			category: "参加について",
			icon: "👥",
			questions: [
				{
					id: "fee",
					q: "参加費用はかかりますか？",
					a: "基本的な参加費用は無料です。ただし、以下の場合は費用が発生することがあります：\n・特別イベント（500円〜2,000円）\n・教材費（実費）\n・資格試験対策講座（別途案内）",
				},
				{
					id: "beginner",
					q: "初心者でも参加できますか？",
					a: "はい、初心者の方も大歓迎です。以下のサポート体制を整えています：\n・初心者向けの入門講座\n・メンターによる個別サポート\n・段階的な学習プランの提供",
				},
				{
					id: "age",
					q: "年齢制限はありますか？",
					a: "一般メンバーは18歳以上、学生メンバーは15歳以上（中学生以上）から参加可能です。ただし、未成年の方は保護者の同意が必要です。",
				},
				{
					id: "location",
					q: "大船渡市外からでも参加できますか？",
					a: "はい、市外からの参加も歓迎です。オンラインでの参加も可能で、以下の方法で参加できます：\n・オンラインもくもく会\n・ハイブリッド形式の勉強会\n・オンラインメンタリング",
				},
			],
		},
		{
			id: "activities",
			category: "活動内容",
			icon: "📚",
			questions: [
				{
					id: "themes",
					q: "どんな学習テーマに取り組めますか？",
					a: "以下のような幅広い分野での学習が可能です：\n\n【テクノロジー】\n・プログラミング（Web、アプリ開発）\n・データサイエンス\n・AI/機械学習\n\n【クリエイティブ】\n・Webデザイン\n・動画編集\n・イラスト制作\n\n【ビジネス】\n・マーケティング\n・起業・副業\n・プロジェクト管理",
				},
				{
					id: "schedule",
					q: "活動の頻度はどのくらいですか？",
					a: "主な活動スケジュールは以下の通りです：\n\n・もくもく会：毎週土曜 13:00-17:00\n・勉強会：月1回 19:00-21:00\n・交流会：月1回\n・特別イベント：不定期\n\n参加は任意で、都合の良い時に参加いただけます。",
				},
				{
					id: "online",
					q: "オンラインでの参加方法を詳しく教えてください",
					a: "オンライン参加には以下のツールを使用します：\n\n・ビデオ会議：Zoom\n・コミュニケーション：Slack\n・資料共有：Google Drive\n・課題管理：Notion\n\n事前に使い方のガイダンスも実施しています。",
				},
			],
		},
		{
			id: "facilities",
			category: "設備・環境",
			icon: "🏢",
			questions: [
				{
					id: "equipment",
					q: "必要な機材は何ですか？",
					a: "基本的に以下の機材をご用意ください：\n\n【必須】\n・ノートPCまたはタブレット\n・イヤホンまたはヘッドセット\n\n【推奨】\n・Webカメラ\n・外付けキーボード\n\n機材の貸し出しも限定的に行っています。",
				},
				{
					id: "wifi",
					q: "会場のネットワーク環境について教えてください",
					a: "会場では以下のネットワーク環境を提供しています：\n\n・高速Wi-Fi（光回線）\n・有線LAN接続可能\n・電源タップ完備\n・バックアップ回線あり",
				},
				{
					id: "space",
					q: "学習スペースの利用時間は？",
					a: "学習スペースは以下の時間で利用可能です：\n\n平日：10:00-21:00\n土日祝：9:00-18:00\n\n※イベント開催時は利用時間が変更になる場合があります。",
				},
			],
		},
		{
			id: "support",
			category: "サポート体制",
			icon: "🤝",
			questions: [
				{
					id: "mentor",
					q: "メンターのサポート体制について教えてください",
					a: "以下のようなメンターサポートを提供しています：\n\n・技術相談：随時（Slack）\n・学習相談：予約制（月2回まで）\n・キャリア相談：予約制（月1回）\n\n各分野の経験者がメンターとして在籍しています。",
				},
				{
					id: "curriculum",
					q: "学習カリキュラムはありますか？",
					a: "はい、以下のような学習支援を提供しています：\n\n・レベル別ロードマップ\n・推奨学習教材リスト\n・課題プロジェクト\n・進捗管理ツール\n\n個人の目標に合わせてカスタマイズも可能です。",
				},
				{
					id: "certificate",
					q: "資格取得のサポートはありますか？",
					a: "はい、以下のようなサポートを提供しています：\n\n・資格試験対策講座\n・模擬試験の実施\n・学習進捗管理\n・受験相談\n\n主要な IT 資格の取得をサポートしています。",
				},
			],
		},
	];

	return (
		<Layout>
			<div className="w-full max-w-4xl mx-auto px-4 py-8 md:py-16">
				<h1 className="text-4xl font-bold text-center mb-12">よくある質問</h1>

				<Tabs defaultValue={faqs[0].id} className="w-full">
					<TabsList className="grid w-full grid-cols-2 lg:grid-cols-4">
						{faqs.map((category) => (
							<TabsTrigger key={category.id} value={category.id}>
								<span className="mr-2">{category.icon}</span>
								{category.category}
							</TabsTrigger>
						))}
					</TabsList>

					{faqs.map((category) => (
						<TabsContent key={category.id} value={category.id}>
							<div className="mt-6">
								<Accordion type="single" collapsible className="space-y-4">
									{category.questions.map((faq) => (
										<AccordionItem key={faq.id} value={faq.id}>
											<AccordionTrigger className="text-left">
												{faq.q}
											</AccordionTrigger>
											<AccordionContent className="whitespace-pre-line">
												{faq.a}
											</AccordionContent>
										</AccordionItem>
									))}
								</Accordion>
							</div>
						</TabsContent>
					))}
				</Tabs>

				<div className="mt-12 p-6 bg-gray-50 rounded-lg">
					<p className="text-center text-gray-600">
						その他のご質問がございましたら、
						<a href="/contact" className="text-blue-500 hover:underline">
							お問い合わせフォーム
						</a>
						からお気軽にご連絡ください。
					</p>
				</div>
			</div>
		</Layout>
	);
};

export default FAQPage;
